import { usePartDefinition } from "@project/emerge";
import { useMemo } from "react";
import * as THREE from "three";
import { PaddingStyle } from "../enum/PaddingStyle";
import { LTPartType } from "../enum/PartType";
import type { PadDefinition } from "../interfaces/PadProperties";
import {
  getEggboxFoamMaterial,
  getSlottedFoamMaterial,
  getSoftFoamMaterial,
  getStandardFoamMaterial,
} from "../materials/FoamMaterials";
import {
  createPanelGeometryWithHoles,
  filterDuplicateCutouts,
} from "./PanelRenderer";

export const PadRenderer = ({ id }: { id: string }) => {
  const partDef = usePartDefinition<PadDefinition & { hidden?: boolean }>({
    id,
    select: [
      "size",
      "strength",
      "style",
      "hidden",
      "type",
      "cutouts",
      "position",
      "side",
      "orientation",
    ] as const,
  });

  const { uniqueCutouts } = useMemo(
    () => filterDuplicateCutouts(partDef?.cutouts ?? []),
    [partDef?.cutouts],
  );

  const padGeometry = useMemo(() => {
    if (partDef?.type !== LTPartType.Pad) {
      return null;
    }
    const w = partDef.size?.[0] ?? 100;
    const h = partDef.size?.[1] ?? 100;
    const str = partDef.strength;
    if (
      !partDef.size ||
      !partDef.position ||
      partDef.side === undefined ||
      str === undefined
    ) {
      return null;
    }
    if (!uniqueCutouts || uniqueCutouts.length === 0) {
      return new THREE.BoxGeometry(w, str, h);
    }
    const v2 = new THREE.Vector2(w, h);
    const pos = partDef.position as THREE.Vector3;
    return createPanelGeometryWithHoles(
      v2,
      pos,
      partDef.side,
      uniqueCutouts,
      str,
    );
  }, [
    partDef?.type,
    partDef?.size,
    partDef?.position,
    partDef?.side,
    partDef?.strength,
    uniqueCutouts,
  ]);

  // Panelgröße in X/Z für konsistente Texturskalierung
  const panelSize = useMemo(() => {
    const w = partDef?.size?.[0];
    const h = partDef?.size?.[1];
    if (typeof w === "number" && typeof h === "number") {
      return new THREE.Vector2(w, h);
    }
    return undefined;
  }, [partDef?.size]);

  const material = useMemo(() => {
    switch (partDef?.style) {
      case PaddingStyle.EGGBOX:
        return getEggboxFoamMaterial(panelSize);
      case PaddingStyle.SOFT:
        return getSoftFoamMaterial(panelSize);
      case PaddingStyle.SLOTTED:
        return getSlottedFoamMaterial(panelSize);
      default:
        return getStandardFoamMaterial(panelSize);
    }
  }, [partDef?.style, panelSize]);

  if (!partDef) return null;

  const { hidden, type } = partDef;

  if (type !== LTPartType.Pad) {
    const group = new THREE.Group();
    group.visible = !hidden;
    const box = new THREE.Mesh(
      new THREE.BoxGeometry(50, 10, 50),
      new THREE.MeshStandardMaterial({ color: "#ff0000" }),
    );
    group.add(box);
    return <primitive object={group} />;
  }

  if (!padGeometry) return null;

  const orientationDeg = partDef.orientation ?? 0;
  const orientationRad = (orientationDeg * Math.PI) / 180;

  const hasCutouts = uniqueCutouts.length > 0;

  /** Part position is slab center (see `applyPadVolumeCenterWorldOffset` in build steps); mesh stays at origin. */
  const meshProps = {
    rotation: hasCutouts
      ? ([Math.PI / 2, 0, orientationRad] as [number, number, number])
      : ([0, 0, 0] as [number, number, number]),
    position: [0, 0, 0] as [number, number, number],
  };

  const primitiveProps = {
    object: padGeometry,
    attach: "geometry" as const,
  };

  const edgesGeometryProps = {
    args: [padGeometry, 10] as [THREE.BufferGeometry, number],
  };

  return (
    <group visible={hidden !== true}>
      <mesh {...meshProps} material={material}>
        <primitive {...primitiveProps} />
      </mesh>
      <lineSegments {...meshProps}>
        <edgesGeometry {...edgesGeometryProps} />
        <lineBasicMaterial color="black" />
      </lineSegments>
    </group>
  );
};
